import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import DupontDatePicker from "../DatePopover/DupontDatePicker";
import CustomModal from "../../packages/CustomModal";
import DPCard from "../../packages/DPCard";
import DupontRadio from "../DupontRadio";
import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";
import CIPSection from "../../pages/defaultCIPInformation/CIPSection";
import WPTextArea from "../../packages/WPTextArea";

import { DatePickerConfig } from "../DatePopover/DateTimeConfig";
import { TextFieldValidator } from "../InputBox/TextFieldValidator";
import { DURATION_CONFIG, ADD_INFO } from "./CIPEvent.config";
import {
  editCip,
  uploadCips,
  getPredictLimits,
  uploadRecurrentCips,
  getadvanceSettings,
} from "../../network/cipAPI";
import {
  DEFAULT_CIP_KEYS,
  CustomModalButtons,
  CIP_EVENT_TYPES,
  CIP_SOURCE_OPTIONS,
} from "../../constants/global.constants";
import { ValueStrings } from "../../utils/constantsString";
import {
  initializeDate,
  formatDate,
  convertMomentToDate,
} from "../../utils/Utils";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import "./CIPEventDialog.scss";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import { CIP_MISSING_VALIDATION } from "../../constants/global.constants";

const CIPEventDialog = ({
  editedRow,
  onModalClose,
  showLoader,
  refreshTable,
  source,
}) => {
  const { t } = useTranslation();

  const {
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    add_info,
    selTrain: selectedTrain,
    minDaysBetweenCIPs,
    maxDaysBetweenCIPs,
  } = useSelector((state) => state);

  const [startDate, setStartDate] = useState(() =>
    editedRow ? initializeDate(editedRow.start_date) : null
  );
  const [endDate, setEndDate] = useState(() =>
    editedRow ? initializeDate(editedRow.end_date) : null
  );
  const [type, setType] = useState(
    editedRow && editedRow.type ? editedRow.type : "C"
  );
  const [daysBetweenCIPsRange, setDaysBetweenCIPsRange] = useState({
    min: minDaysBetweenCIPs,
    max: maxDaysBetweenCIPs,
  });

  const { duration_per_event, duration_between_event } = DURATION_CONFIG(
    t,
    CIP_EVENT_TYPES[type],
    daysBetweenCIPsRange
  );

  const setCipModalInitialValues = () => ({
    type: "C",
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    add_info,
    duration_per_event: duration_per_event.default,
    duration_between_event: duration_between_event.default,
  });

  const [cipModal, setCipModal] = useState(
    editedRow || setCipModalInitialValues()
  );

  const [errors, setErrors] = useState({
    acid_used_per_cip: null,
    alkali_used_per_cip: null,
    additional_chemical_per_cip: null,
    acid_cip_ph: null,
    alkali_cip_ph: null,
    cip_solution_ph: null,
    acid_cip_temp: null,
    alkali_cip_temp: null,
    cip_solution_temp: null,
    acid_cip_duration: null,
    alkali_cip_duration: null,
    cip_solution_duration: null,
    duration_per_event: null,
    duration_between_event: null,
  });

  const [minDateLimit, setMinDateLimit] = useState(null);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [startDateError, setStartDateError] = useState("");
  const [noError, setNoError] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditDataChanged, setIsEditDataChanged] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);

  const { DATE_TIME_FORMAT } = ValueStrings;

  const onClose = ({ target }) => {
    if (target.id === CustomModalButtons.CONFIRM) {
      saveEventHandler();
    } else {
      onModalClose();
    }
  };

  const saveEventHandler = async (forceSubmit = false) => {
    showLoader && showLoader(true);
    try {
      const response = await (editedRow
        ? editCip(cipModal.cip_id, cipModal, selectedTrain)
        : source === CIP_SOURCE_OPTIONS.RECURRENT
        ? uploadRecurrentCips(cipModal, selectedTrain, forceSubmit ? 1 : 0)
        : uploadCips(cipModal, selectedTrain));
      if (response) {
        if (response.cip_id || response.length > 0) {
          // Wait for the API response to be fully processed before refreshing table
          if (refreshTable) {
            await refreshTable();
          }

          onModalClose();
          const key = editedRow
            ? "CIPEvent.editedSuccess"
            : source === CIP_SOURCE_OPTIONS.RECURRENT
            ? "CIPEvent.recurrentSuccess"
            : "CIPEvent.addedSuccess";
          createNotification(
            SUCCESS_NOTIFICATION,
            t(key, {
              eventType: CIP_EVENT_TYPES[type],
            })
          );
        } else {
          throw new Error(response.detail?.error);
        }
      }
    } catch (err) {
      showLoader && showLoader(false);
      const missingCipsErrorMessage = err.message || err.Error || "";
      const isValidationError =
        missingCipsErrorMessage === CIP_MISSING_VALIDATION.ERROR_MESSAGE;

      if (isValidationError && !forceSubmit) {
        setShowValidationWarning(true);
        return;
      }

      const errorMsg = err.message || t("notificationStrings.somethingWrong");
      createNotification(ERROR_NOTIFICATION, errorMsg);
      onModalClose();
    }
  };

  const onStartDateChange = (value) => {
    const isoDate = formatDate(value);
    setStartDate(value);
    setStartDateError("");
    setCipModal({
      ...cipModal,
      start_date: isoDate,
    });
    if (Date.parse(value) >= Date.parse(endDate)) {
      setStartDateError(t("CIPEvent.startDateError"));
    }
  };

  const onEndDateChange = (value) => {
    const isoDate = formatDate(value);
    setEndDate(value);
    setStartDateError("");
    setCipModal({ ...cipModal, end_date: isoDate });
  };

  const handleRadioChange = (event) => {
    const { name } = event.target;
    setCipModal({ ...cipModal, type: name });
    setType(name);
  };

  const handleInputAddChange = (key, value) => {
    setCipModal({ ...cipModal, [key]: value });
  };

  const handleValidationWarningClose = (buttonId) => {
    setShowValidationWarning(false);
    if (buttonId === CustomModalButtons.CANCEL) {
      onModalClose();
      saveEventHandler(true);
    }
  };

  const handleInputChange = (field, value, minValue, maxValue) => {
    setErrors({ ...errors, [field]: null });
    setCipModal({ ...cipModal, [field]: value || null });

    const fieldName = t(`defaultInformationStrings.${field}`).split("(")[0];

    if ((value === "" && minValue !== null) || parseFloat(value) < minValue) {
      setErrors({
        ...errors,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue}`,
      });
    } else if (
      maxValue !== null &&
      (parseFloat(value) < minValue || parseFloat(value) > maxValue)
    ) {
      setErrors({
        ...errors,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue} ${t(
          "defaultInformationStrings.greaterThan"
        )} ${maxValue}`,
      });
    }
  };

  const heading = t("CIPEvent.heading", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const headerHelper = t("CIPEvent.description", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const CIPEventHeader = <div className="heading">{heading}</div>;
  const CIPEventHeaderHelper = (
    <div className="header-helper">{headerHelper}</div>
  );

  const fetchDateLimits = async () => {
    try {
      const limits = await getPredictLimits(selectedTrain);
      if (limits.data) {
        setMinDateLimit(convertMomentToDate(limits.data.min_date));
        setMaxDateLimit(convertMomentToDate(limits.data.max_date));
      } else {
        throw new Error("error");
      }
    } catch (err) {
      setLoading(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong")
      );
      onModalClose();
    }
  };

  const fetchDurationLimits = async () => {
    try {
      const advanceSettings = await getadvanceSettings(selectedTrain);
      if (advanceSettings?.data?.settings) {
        const { min_day_between_cips, max_days_between_cips } =
          advanceSettings.data.settings;

        setDaysBetweenCIPsRange({
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        const durationConfig = DURATION_CONFIG(t, CIP_EVENT_TYPES[type], {
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        setCipModal({
          ...cipModal,
          duration_between_event: durationConfig.duration_between_event.default,
        });
      } else {
        throw new Error("error");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong")
      );
      onModalClose();
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 700);
    }
  };

  // Compare cipModal with editedRow for changes
  const editDataChangedHandler = () => {
    if (!editedRow) return false;

    const keys = Object.keys(cipModal);
    for (let key of keys) {
      if (key === "start_date" || key === "end_date") {
        if (formatDate(editedRow[key]) !== formatDate(cipModal[key])) {
          return true;
        }
      } else if ((editedRow[key] ?? "") != (cipModal[key] ?? "")) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null
    );
    setNoError(noError);
  }, [errors]);

  useEffect(() => {
    setLoading(true);
    if (source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow) {
      Promise.all([fetchDateLimits(), fetchDurationLimits()]);
    } else {
      fetchDateLimits().finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (editedRow) {
      setIsEditDataChanged(editDataChangedHandler());
    }
  }, [cipModal, editedRow]);

  useEffect(() => {
    if (!editedRow) {
      if (type === "C") {
        setCipModal(setCipModalInitialValues());
      } else {
        setCipModal({
          type,
          start_date: startDate,
          end_date: endDate,
          duration_per_event: duration_per_event.default,
          duration_between_event: duration_between_event.default,
        });
      }
    }
  }, [type]);

  return (
    <CustomModal
      header={CIPEventHeader}
      headerHelper={CIPEventHeaderHelper}
      confirmBtn={t("CIPEvent.saveEvent")}
      cancelBtn={t("CIPEvent.close")}
      onModalClose={onClose}
      width="1050px"
      customclass="cip-event-dialog"
      confirmBtnDisabled={
        !startDate ||
        startDateError ||
        !noError ||
        (editedRow ? !isEditDataChanged : false) ||
        (source === CIP_SOURCE_OPTIONS.RECURRENT &&
          !editedRow &&
          (!cipModal.duration_per_event || !cipModal.duration_between_event))
      }
      isLoading={loading}
    >
      <DPCard>
        <div className="cip-event-fields">
          <div>
            <div className="field-name">{t("predictionStrings.type")}</div>
            <div className="radio-container" onChange={handleRadioChange}>
              <DupontRadio
                text={t("CIPEvent.CIP")}
                checked={type === "C"}
                onClick={() => setType("C")}
                name="C"
                value="C"
              />
              <DupontRadio
                text={t("CIPEvent.shutdown")}
                checked={type === "S"}
                onClick={() => setType("S")}
                name="S"
                value="S"
              />
            </div>
          </div>
          <div className="vertical-seperator"></div>
          <div className="date-fields-container">
            <DupontDatePicker
              selected={startDate}
              onChange={onStartDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              minDate={minDateLimit}
              maxDate={maxDateLimit}
              label={`${CIP_EVENT_TYPES[type]} ${t("CIPEvent.startDate")}`}
              labelClass="dupont-form-label wrapText text-left"
              className={"text-left width-fill"}
              error={startDateError}
              isRequired
              placeholderText="Select date and time"
              {...DatePickerConfig}
            />
            <DupontDatePicker
              selected={endDate}
              onChange={onEndDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              label={`${CIP_EVENT_TYPES[type]} ${t("CIPEvent.endDate")}`}
              disabled={!Boolean(startDate)}
              minDate={startDate}
              labelClass="dupont-form-label wrapText text-left"
              className={"width-fill"}
              placeholderText="Select date and time"
              {...DatePickerConfig}
            />
          </div>

          {source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow && (
            <>
              <div className="vertical-seperator"></div>
              <div className="duration-fields-container">
                {[duration_per_event, duration_between_event].map((field) => (
                  <WPRangedInput
                    key={field.key}
                    value={cipModal[field.key]}
                    name={field.key}
                    label={field.label}
                    suffix={field.unit}
                    onChange={(e) =>
                      handleInputChange(
                        field.key,
                        e.target.value,
                        field.ranges?.hard?.min,
                        field.ranges?.hard?.max
                      )
                    }
                    ranges={field.ranges}
                    maxLength={10}
                    required
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DPCard>
      {type === "C" && (
        <div className="marginTop10">
          <CIPSection
            defaultCIPKeys={DEFAULT_CIP_KEYS}
            errors={errors}
            existingCIPState={editedRow}
            handleInputChange={handleInputChange}
          />
        </div>
      )}
      <div className="margin-top-10">
        <WPTextArea
          value={cipModal[ADD_INFO.key]}
          label={t(`CIPEvent.addInfo`)}
          rows={ADD_INFO.multiline}
          onChange={(value) => handleInputAddChange(ADD_INFO.key, value)}
          maxCount={ADD_INFO.maxChar}
          placeholder={t(`CIPEvent.addInfoDefaultText`)}
        />
      </div>
      {showValidationWarning && (
        <ConfirmationPopup
          open={showValidationWarning}
          type={DIALOG_TYPES.WARNING}
          header={t("CIPEvent.missingCIPHeader")}
          description={t("CIPEvent.missingCIPDescription")}
          onClose={handleValidationWarningClose}
          cancelBtn={t("CIPEvent.missingCIPCancelBtn")}
          confirmBtn={t("CIPEvent.missingCIPConfirmBtn")}
          customBackdrop={true}
        ></ConfirmationPopup>
      )}
    </CustomModal>
  );
};

export default CIPEventDialog;
